// ============================================================================
// Recurring-donation config for the donor app.
// Programs and the monthly MINIMUM are maintained by the Admin in the CMS and
// served by the public list_programs endpoint (type=program + config C1 in
// dev_pnb). This module fetches them and exposes a label helper. The donor app
// redirects to Stripe hosted Checkout, so no card data is handled here.
// ============================================================================
import { useCallback, useEffect, useState } from "react";
import { useAws } from "../aws";

// Default general-purpose option, prepended client-side to the CMS program list.
export const ANY_PROGRAM = { id: "ANY", name: "一般用途", en: "" };

// Fallback minimum monthly amount if the config item cannot be read.
export const DEFAULT_MONTHLY_MIN = 100;

// Render the general-purpose programme uniformly as 一般用途, whether a record
// stored the legacy "不指定 Any" value or nothing at all.
export const displayProgramName = (name) =>
  !name || /不指定|<any>|^\s*any\s*$/i.test(name) ? "一般用途" : name;

// Loads the active program list + monthly minimum from the public endpoint.
// Returns { programs (incl. the general-purpose option), monthlyMin, programLabel, loaded }.
export const useProgramConfig = () => {
  const { awsHttp } = useAws();
  const [programs, setPrograms] = useState([ANY_PROGRAM]);
  const [monthlyMin, setMonthlyMin] = useState(DEFAULT_MONTHLY_MIN);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    awsHttp({ HTTPRequestMethod: "GET", APIFunction: "user/list_programs", unauthorize: true, requestBody: null })
      .then((res) => {
        if (!alive) return;
        const body = res?.data?.body || {};
        const list = Array.isArray(body.programs) ? body.programs : [];
        setPrograms([
          ANY_PROGRAM,
          ...list.map((p) => ({ id: p.id, name: p.name || "", en: p.en_name || "", code: p.program_code || "" })),
        ]);
        // Accept the new monthly_min field; fall back to a legacy monthly_cap value.
        const min = body.monthly_min != null ? body.monthly_min : body.monthly_cap;
        if (min != null) setMonthlyMin(Number(min));
      })
      .catch(() => { setError(true);
        // Keep the general-purpose-only default so the form still works if the API is unreachable.
      })
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
    // useAws() returns a fresh awsHttp each render, so depending on it would
    // re-fetch forever. Load the program list once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const programLabel = useCallback(
    (id) => {
      const p = programs.find((x) => x.id === id) || ANY_PROGRAM;
      return p.id === "ANY" ? "一般用途" : `${p.name} ${p.en}`.trim();
    },
    [programs]
  );

  return { programs, monthlyMin, programLabel, loaded, error };
};
